import { ReactNode, useState } from 'react';
import { IoArrowForward, IoClose, IoQrCodeSharp } from 'react-icons/io5';

import { ActionIcon, Alert, Anchor, Box, Group, Modal, Stack, Text, ThemeIcon } from '@mantine/core';
import { z } from 'zod';

import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { TextField } from 'components/forms/fields/TextField';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { CreateAccountField } from 'components/showtime/components/CreateAccountField';
import { useIsMobile } from 'hooks/use-is-mobile';
import { FeedbackService } from 'services/FeedbackService';

import cx from './UserInfoModal.module.scss';

const UserInfoModal = ({
    opened,
    defaultValues,
    onSubmit,
    onClose,
    intro,
}: {
    opened: boolean;
    defaultValues: Partial<UserInfo>;
    onSubmit: (userInfo: UserInfo) => Promise<void>;
    onClose?: () => void;
    intro?: ReactNode;
}) => {
    const isMobile = useIsMobile('xs');

    const [accountFailed, setAccountFailed] = useState(false);

    const submit = async (userInfo: UserInfo) => {
        setAccountFailed(false);

        try {
            await onSubmit(userInfo);
        } catch {
            setAccountFailed(true);
        }
    };

    return (
        <Modal
            zIndex={300}
            opened={opened}
            onClose={onClose ?? (() => undefined)}
            withCloseButton={false}
            closeOnClickOutside={Boolean(onClose)}
            closeOnEscape={Boolean(onClose)}
            centered={!isMobile}
            size={400}
            padding={0}
            xOffset={isMobile ? 0 : undefined}
            yOffset={isMobile ? 0 : undefined}
            classNames={{ overlay: cx.overlay, content: cx.content }}
            styles={{
                // The theme pads modal bodies regardless of the padding prop
                body: { padding: 0 },
                ...(isMobile && {
                    // Sits at the bottom of the screen like the scan notes card that follows it
                    inner: {
                        alignItems: 'flex-end',
                        padding: '6px 6px calc(6px + env(safe-area-inset-bottom))',
                    },
                    content: { width: '100%', maxWidth: '100%' },
                }),
            }}
        >
            <Group className={cx.header} gap="sm" wrap="nowrap">
                <ReplusLogo w={34} />
                <Box flex={1} miw={0}>
                    <Text size="sm" fw={700} lh={1.3}>
                        RE+ Showtime
                    </Text>
                    <Text size="xs" c="dimmed" lh={1.3}>
                        Your RE+ event companion
                    </Text>
                </Box>
                {onClose && (
                    <ActionIcon variant="subtle" color="gray" radius="md" onClick={onClose} aria-label="Close">
                        <IoClose />
                    </ActionIcon>
                )}
            </Group>

            <Stack className={cx.body} gap="sm">
                {intro ?? (
                    <>
                        <Group
                            gap="sm"
                            wrap="nowrap"
                            p="sm"
                            bg="gray.1"
                            style={{ borderRadius: 'var(--mantine-radius-md)' }}
                        >
                            <ThemeIcon variant="white" color="#5146ff" radius="md" size="lg">
                                <IoQrCodeSharp size={18} />
                            </ThemeIcon>
                            <Text fw={500} fz="sm">
                                First time here? Scan QR codes to save exhibitor profiles & keep notes in one place.
                            </Text>
                        </Group>

                        <Text c="dimmed" fz="sm">
                            Tell us who you are so we can keep your saved profiles and notes safe for you.
                        </Text>
                    </>
                )}

                <Form<UserInfo> zodSchema={UserInfoSchema} onSubmit={submit} defaultValues={defaultValues}>
                    <Stack gap="sm">
                        <TextField size="md" name="name" label="Your name" classNames={{ input: cx.input }} />
                        <TextField
                            size="md"
                            name="email"
                            type="email"
                            autoComplete="email"
                            label="Your email"
                            classNames={{ input: cx.input }}
                        />

                        <CreateAccountField />

                        {accountFailed && (
                            <Alert color="red">
                                We could not create your account. Please try again later or{' '}
                                <Anchor inherit onClick={() => FeedbackService.open()}>
                                    contact support
                                </Anchor>
                                .
                            </Alert>
                        )}

                        <FormSubmit size="md" color="#5146ff" rightSection={<IoArrowForward />}>
                            Continue
                        </FormSubmit>
                    </Stack>
                </Form>
            </Stack>
        </Modal>
    );
};

const UserInfoSchema = z.object({
    name: z.string().min(1, 'Please enter your name'),
    email: z.string().toLowerCase().email('Please enter a valid email'),
    createAccount: z.boolean().optional(),
});

type UserInfo = z.infer<typeof UserInfoSchema>;

export type { UserInfo };
export { UserInfoModal };
